import { AnyAction, createSlice } from '@reduxjs/toolkit';

import { SignatureState } from '@greenkub-fronts/shared/interface';

import initSignaturePageUseCase from './use-cases/init-signature-page.use-case';
import setSignature from './use-cases/set-signature.use-case';
import setSignatures from './use-cases/set-signatures.use-case';
import setHeaderFocus from './use-cases/set-header-focus.use-case';
import openModalSignature from './use-cases/open-modal-signature.use-case';
import onCustomerSign from './use-cases/on-customer-sign.use-case';
import setDocumentIsLoaded from './use-cases/set-document-is-loaded.use-case';
import closeModalSignature from './use-cases/close-modal-signature.use-case';
import onValidateSign from './use-cases/on-validate-sign.use-case';

export const SIGNATURE_FEATURE_KEY = 'signature';

const initialSignatureStateStateInit = (): SignatureState => {
  return {
    toGenerate: false,
    fromExisting: false,
    isSignature: false,
    project: null,
    projectId: null,
    upsell: null,
    tokenIsValid: false,
    signature: '',
    signatures: [false],
    document: null,
    documentIsLoaded: false,
    documentObjectURL: '',
    currentIndexSignature: 0,
    headerFocus: false,
    modalIsOpen: false,
    isSigningDoc: false,
    projectToken: '',
    upsellToken: '',
    documentSlug: '',
    upsellId: '',
    initSignaturePage: false,
  };
};

export const initialSignatureState: SignatureState =
  initialSignatureStateStateInit();

const signatureSlice = createSlice({
  name: SIGNATURE_FEATURE_KEY,
  initialState: initialSignatureStateStateInit,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(initSignaturePageUseCase.pending, (state) => {
      state.initSignaturePage = true;
    });
    builder.addCase(
      initSignaturePageUseCase.fulfilled,
      (state, action: AnyAction) => {
        state.toGenerate = action['payload'].toGenerate;
        state.fromExisting = action['payload'].fromExisting;
        state.isSignature = action['payload'].isSignature;
        state.project = action['payload'].project;
        state.projectId = action['payload'].projectId;
        state.upsell = action['payload'].upsell;
        state.tokenIsValid = action['payload'].tokenIsValid;
        state.signature = action['payload'].signature;
        state.document = action['payload'].document;
        state.documentIsLoaded = action['payload'].documentIsLoaded;
        state.documentObjectURL = action['payload'].documentObjectURL;
        state.projectToken = action['payload'].projectToken;
        state.upsellToken = action['payload'].upsellToken;
        state.documentSlug = action['payload'].documentSlug;
        state.upsellId = action['payload'].upsellId;
        state.initSignaturePage = false;
      }
    );
    builder.addCase(initSignaturePageUseCase.rejected, (state) => {
      state.initSignaturePage = false;
    });
    builder.addCase(setSignature.fulfilled, (state, action) => {
      state.signature = action['payload'].signature;
    });
    builder.addCase(setSignatures.fulfilled, (state, action) => {
      state.signatures = action['payload'].signatures;
    });
    builder.addCase(setHeaderFocus.fulfilled, (state, action) => {
      state.headerFocus = action['payload'].headerFocus;
    });
    builder.addCase(openModalSignature.fulfilled, (state, action) => {
      state.currentIndexSignature = action['payload'].currentIndexSignature;
      state.modalIsOpen = true;
    });
    builder.addCase(closeModalSignature.fulfilled, (state, action) => {
      state.modalIsOpen = false;
    });
    builder.addCase(setDocumentIsLoaded.fulfilled, (state, action) => {
      state.documentIsLoaded = action['payload'].documentIsLoaded;
    });
    builder.addCase(onCustomerSign.pending, (state, action) => {
      state.isSigningDoc = true;
    });
    builder.addCase(onCustomerSign.rejected, (state, action) => {
      state.isSigningDoc = false;
    });
    builder.addCase(onCustomerSign.fulfilled, (state, action) => {
      state.isSigningDoc = false;
      state.documentIsLoaded = action['payload']?.isDocumentLoaded;
      state.document = action['payload']?.document;
    });
    builder.addCase(onValidateSign.fulfilled, (state, action) => {
      state.signatures[state.currentIndexSignature] = true;
      state.modalIsOpen = false;
    });
  },
});

export const signature = signatureSlice.reducer;

export const signatureActions = signatureSlice.actions;
